import { ChangeEvent, useState, DragEvent, MouseEvent } from "react";
import { FieldValues, useController } from "react-hook-form";
import styled, { css } from "styled-components";

import { SrOnly } from "@/components/Layout/Parts";
import { ReactComponent as UploadIcon } from "@/assets/icons/upload.svg";
import { ReactComponent as FilePlaceholder } from "@/assets/icons/file-placeholder.svg";
import { ReactComponent as ImgIcon } from "@/assets/icons/img-placeholder.svg";
import { ReactComponent as VideoIcon } from "@/assets/icons/video-placeholder.svg";
import Text from "@/components/Text";
import { DefaultProps, FileFieldProps } from "../FormField";
import { humanFileSize } from "@/utils/helpers";

export const Dropzone = styled.div<{ $dragging: boolean }>`
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  ${({ theme, $dragging }) =>
    $dragging &&
    css`
      border-color: ${theme.colors.purpleLight};
    `}
`;

export const Title = styled(Text)`
  margin: 10px 0 6px;
`;

export const Notice = styled(Text)`
  opacity: 0.5;
  text-align: center;
`;

const Preview = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  padding: 16px;
  position: relative;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
  padding-left: 16px;
  padding-right: 24px;
`;

const FileName = styled(Text)`
  margin-bottom: 8px;
  overflow-wrap: anywhere;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  transition: 0.2s ease-out opacity;
  &:hover {
    opacity: 0.75;
  }
`;

export const InnerLabel = styled(Text)`
  cursor: pointer;
`;

type Props<T extends FieldValues> = Omit<
  DefaultProps<T> & FileFieldProps,
  "type"
>;

const SingleFileField = <T extends FieldValues>({
  name,
  control,
  existingMedia,
  accept = "*",
  notes = <>Supports: Any file up to 10MB</>,
  className,
  id,
  icon,
  onRemoveExisting,
}: Props<T>) => {
  const [dragging, setDragging] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!existingMedia);

  const { field } = useController({
    name,
    control,
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    field.onChange(e.target.files?.[0] ?? null);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.items?.length) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer?.files?.length) {
      const droppedFile = e.dataTransfer.files[0];
      field.onChange(droppedFile);
      e.dataTransfer?.clearData();
    }
  };

  const onClearFile = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isEditMode) {
      setIsEditMode(true);
      onRemoveExisting?.();
    } else {
      field.onChange(null);
    }
  };

  const renderIcon = () => {
    switch (icon) {
      case "image":
        return <ImgIcon />;
      case "video":
        return <VideoIcon />;
      default:
        return <UploadIcon />;
    }
  };

  const previewable = field.value
    ? field.value
    : !isEditMode
    ? existingMedia
    : null;

  return (
    <div className={className}>
      <>
        {previewable ? (
          <Preview>
            <ClearButton onClick={onClearFile}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12"
                  stroke="#222222"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 12L4 4"
                  stroke="#222222"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ClearButton>
            <FilePlaceholder />
            <FileInfo>
              <FileName as="div" $type="smallMedium">
                {previewable.name}
              </FileName>
              <Text as="div" $type="smallMedium" $color="gray">
                {humanFileSize(previewable.size)}
              </Text>
            </FileInfo>
          </Preview>
        ) : (
          <Dropzone
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            $dragging={dragging}
          >
            {renderIcon()}
            <Title $type="smallBold" $color="gray" as="div">
              Drop file here or{" "}
              <InnerLabel
                as="label"
                $type="smallBold"
                $color="purple"
                htmlFor={id}
              >
                browse
                <SrOnly
                  as="input"
                  type="file"
                  accept={accept}
                  id={id}
                  onBlur={field.onBlur}
                  onChange={onChange}
                />
              </InnerLabel>
            </Title>
            <Notice $type="extraSmall" $color="gray" as="div">
              {notes}
            </Notice>
          </Dropzone>
        )}
      </>
    </div>
  );
};

export default SingleFileField;
